/* eslint-disable react-hooks/rules-of-hooks */

import firebase from "firebase/compat";
import { useTranslation } from "react-i18next";
import useSettings from "hooks/useSettings";
import { fNumber } from "../formatNumber";
import { fDate } from "utils/formatTime";
import useLocales from "hooks/useLocales";
import { formatLeaseDay, israelDayOf } from "./lease";
import { isRentedListing } from "../../@types/sharedSchema";
import type { CSSProperties } from "react";
import type { SvgIconComponent } from "@material-ui/icons";
import type { LabelColor } from "components/Label";
import type { Listing } from "../../@types/listings/listing";
import type { AddressDisplayMode, TransactionType } from "../../@types/sharedSchema";
import type {
  ListingStatus,
  ProcessedListingAddress,
  FavoriteListingsOfContact
} from "../../@types/sharedSchema";

export function getFullAddress({
  address,
  street,
  neighborhood,
  city
}: Partial<Record<"address" | "street" | "neighborhood" | "city", string>>): string {
  return [address, street, neighborhood, city].filter(Boolean).join(", ");
}

export function clampLineStyle(lineClamp: number): CSSProperties {
  return {
    display: "-webkit-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    WebkitLineClamp: lineClamp,
    WebkitBoxOrient: "vertical"
  };
}

export type PropertyDetailsProps = {
  listing: Listing;
  isAuthorized?: boolean;
};

export type Details = {
  title: string;
  value: string | number;
  Icon?: SvgIconComponent;
  usingRTL?: boolean;
};

export function createAdditionalPropertyDetailsForRealtor({
  listing,
  isAuthorized
}: PropertyDetailsProps): Details[] {
  const {
    fee,
    cleaningFee,
    exclusivity,
    projectName,
    leaseEndDate,
    leaseStartDate,
    feeIncludesTaxes,
    municipalityTaxes,
    addressAreaNickname,
    userDefinedListingId
  } = listing;

  const { t: translate } = useTranslation();
  const { currentLang } = useLocales();

  // The rental agreement, on a rented listing only — and censored for the same viewers the
  // exclusivity date is.
  const leaseStart = leaseStartDate ? formatLeaseDay(israelDayOf(leaseStartDate.toDate())) : null;
  const leaseEnd = leaseEndDate ? formatLeaseDay(israelDayOf(leaseEndDate.toDate())) : null;

  let leaseTerm = translate("listingsPage.lease.detailsMissing");

  if (leaseStart && leaseEnd) {
    leaseTerm = translate("listingsPage.lease.detailsRange", { start: leaseStart, end: leaseEnd });
  } else if (leaseEnd) leaseTerm = translate("listingsPage.lease.detailsUntil", { end: leaseEnd });
  else if (leaseStart) {
    leaseTerm = translate("listingsPage.lease.detailsFrom", { start: leaseStart });
  }

  const leaseDetails: Details[] = isRentedListing(listing)
    ? [
        {
          title: translate("listingsPage.lease.title"),
          value: censorStringToAsterisks(!isAuthorized, leaseTerm, "exclusivity")
        }
      ]
    : [];

  return [
    {
      title: translate("listingsPage.tableColumns.exclusivity"),
      value: censorStringToAsterisks(
        !isAuthorized,
        exclusivity?.toDate?.()?.toLocaleDateString?.(currentLang.dateLocale) ?? null,
        "exclusivity"
      )
    },
    ...leaseDetails,
    {
      title: translate("listingsPage.tableColumns.fee"),
      value: censorStringToAsterisks(
        !isAuthorized,
        fee
          ? `${fee}%${
              feeIncludesTaxes
                ? ` (${translate("listingsPage.tableColumns.feeIncludesTaxes")})`
                : ""
            }`
          : "-",
        "fee"
      )
    },
    {
      title: translate("listingsPage.tableColumns.addressAreaNickname"),
      value: addressAreaNickname ? addressAreaNickname : "-"
    },
    {
      title: translate("listingsPage.tableColumns.cleaningFee"),
      value: numberToCurrency(cleaningFee)
    },
    {
      title: translate("listingsPage.tableColumns.municipalityTaxes"),
      value: numberToCurrency(municipalityTaxes)
    },
    {
      title: translate("listingsPage.tableColumns.projectName"),
      value: projectName ? projectName : "-"
    },
    {
      title: translate("listingsPage.tableColumns.userDefinedListingId"),
      value: userDefinedListingId ? userDefinedListingId : "-"
    }
  ];
}

export function boolToYesNo(value: boolean): string {
  const { t: translate } = useTranslation();

  return value ? translate("general.yes") : "-";
}

export function numberToCurrency(value: number | null): string {
  return value ? `₪${fNumber(value)}` : "-";
}

/**
 * "הרצל 15, תל אביב", falling back through the fields realtors actually fill
 * in. Unlike `getFullAddress` it carries the house number and never returns an
 * empty string, so it is safe as a row title.
 */
export function buildListingDisplayName(listing: Listing, fallback: string): string {
  const clean = (value?: string | number | null): string =>
    value === undefined || value === null ? "" : String(value).trim();

  const street = [clean(listing.address), clean(listing.streetNumber)].filter(Boolean).join(" ");
  const locality = clean(listing.city) || clean(listing.neighborhood);
  const streetAndCity = [street, locality].filter(Boolean).join(", ");

  return (
    streetAndCity ||
    clean(listing.projectName) ||
    clean(listing.addressAreaNickname) ||
    clean(listing.userDefinedListingId) ||
    fallback
  );
}

/**
 * Status → chip colour. Mirrors StatusDistributionBar's palette so the bar and
 * the listings table inside the same modal can never disagree about what
 * "sold" looks like.
 */
export const LISTING_STATUS_LABEL_COLOR: Record<ListingStatus, LabelColor> = {
  onSale: "info",
  inNegotiation: "warning",
  sold: "success",
  frozen: "default",
  removed: "error"
};

export function timestampToDate(timestamp: firebase.firestore.Timestamp | null): string {
  const { themeDirection } = useSettings();

  if (!timestamp) return "-";

  const isRTL = themeDirection === "rtl";

  return fDate(timestamp.toDate(), isRTL);
}

export function getParsedListingStatusLabel(
  status: ListingStatus,
  transactionType: TransactionType | null
): string {
  let parsedStatus: string = status;

  if (transactionType === "rent") {
    if (status === "onSale") parsedStatus = "forRent";
    if (status === "sold") parsedStatus = "rented";
  }

  return parsedStatus;
}

export function censorStringToAsterisks(
  enabled: boolean,
  sensorString: string,
  type: "fee" | "price" | "exclusivity"
): string {
  if (!enabled) return sensorString;

  let asterisks: string;

  if (type === "fee") asterisks = "***";
  if (type === "price") asterisks = "********";
  if (type === "exclusivity") asterisks = "** *** ****";

  return asterisks;
}

export function formatBytesToHumanReadableSize(
  bytes: number,
  si: boolean = true,
  dp: number = 1
): string {
  const threshold = si ? 1000 : 1024;

  if (Math.abs(bytes) < threshold) return bytes + " B";

  let units: string[];

  if (si) units = ["KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  else units = ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];

  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= threshold;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= threshold && u < units.length - 1);

  return bytes.toFixed(dp) + " " + units[u];
}

export function getFileExtension(fileName: string, maxLength = 3): string {
  return fileName.split(".").pop()?.toUpperCase().slice(0, maxLength) ?? "";
}

type ListingMatchAdditionalDetails = {
  hiddenPictures: string[];
  descriptionOverride: string;
};

export function getListingMatchAdditionalDetails({
  listingId,
  description,
  favoriteListingsOfContact
}: {
  listingId: string;
  description?: string;
  favoriteListingsOfContact: FavoriteListingsOfContact | null;
}): ListingMatchAdditionalDetails | null {
  const additionalDetails = favoriteListingsOfContact?.additionalDetails?.[listingId];

  const parsedDescription = additionalDetails?.descriptionOverride || description;

  if (!additionalDetails) {
    return {
      descriptionOverride: parsedDescription,
      hiddenPictures: []
    };
  }

  const { enabled, pictures } = additionalDetails.hiddenPictures ?? {};

  let parsedHiddenPictures: string[] = [];

  if (enabled && Array.isArray(pictures)) {
    parsedHiddenPictures = pictures;
  }

  return {
    hiddenPictures: parsedHiddenPictures,
    descriptionOverride: parsedDescription
  };
}

export function parseListingAddressByDisplayMode(
  address: ProcessedListingAddress,
  addressDisplayMode: AddressDisplayMode
): ProcessedListingAddress {
  if (addressDisplayMode === "cityNeighborhood") {
    return {
      ...address,
      street: ""
    };
  }

  if (addressDisplayMode === "cityStreet") {
    return {
      ...address,
      neighborhood: ""
    };
  }

  // Full address
  return address;
}
